import { type DragEvent, useEffect, useSyncExternalStore } from "react";
import { ROOT_TARGET, type UploadTarget } from "./uploads.ts";

type DropState = {
  pageTarget: UploadTarget;
  /** Name of the folder a plain upload lands in, so the drop pill can say where. */
  folder: string | null;
  hoverTarget: UploadTarget | null;
  hoverFolder: string | null;
};

let state: DropState = {
  pageTarget: ROOT_TARGET,
  folder: null,
  hoverTarget: null,
  hoverFolder: null,
};
const listeners = new Set<() => void>();

const set = (change: Partial<DropState>) => {
  state = { ...state, ...change };
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useDropState = () => useSyncExternalStore(subscribe, () => state);
export const getDropState = () => state;
export const setHoverTarget = (
  hoverTarget: UploadTarget | null,
  hoverFolder: string | null = null,
) => {
  if (state.hoverTarget !== hoverTarget || state.hoverFolder !== hoverFolder) {
    set({ hoverTarget, hoverFolder });
  }
};

export const carriesFiles = (event: { dataTransfer: DataTransfer | null }) =>
  event.dataTransfer?.types.includes("Files") === true;

/** Dragged item count; browsers expose it during dragover but not the files themselves. */
export const draggedCount = (event: DragEvent) => event.dataTransfer.items.length;

/** Where several files at once land: beside the target, never at the root by surprise. */
export const batchTarget = (target: UploadTarget): UploadTarget =>
  target.kind === "new" ? target : { kind: "new", parentId: target.parentId };

export const usePageDropTarget = (target: UploadTarget, folder: string | null = null) => {
  const kind = target.kind;
  const parentId = target.parentId;
  const nodeId = target.kind === "version" ? target.nodeId : null;
  const name = target.kind === "version" ? target.name : null;
  useEffect(() => {
    set({
      folder,
      pageTarget:
        kind === "version" && nodeId !== null && name !== null
          ? { kind: "version", nodeId, name, parentId }
          : { kind: "new", parentId },
    });
    return () => set({ pageTarget: ROOT_TARGET, folder: null });
  }, [kind, nodeId, name, parentId, folder]);
};
